import React, { useEffect, useState } from "react";
import { Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {THIRD_PARTY_INTEGRATION_LINKS} from "../../common/constants";
import {
  getPublicApiApprovalStatus,
  selector,
} from "./approvePublicApiUserSlice";
import { RootState } from "../../store";
import api from "../../api";
import { getCookie } from "../../common/utils";
import { getIntegrationWebhookStatusUrl } from "../../config";

import "./integrations.scss";
import { FLASHCAST_USER_GUIDE_URL, routes } from "../../common/constants";
import { HelpIcon } from "../../components/ui/icons";

interface IIntegrationWebhookStatus {
  singlewireWebhookUpdateRequired: boolean;
  everbridgeWebhookUpdateRequired: boolean;
}

const Integration = () => {
  const dispatch = useDispatch<RootState>();
  const { isSuperAdminApproved } = useSelector(selector);
  const [webhookStatus, setWebhookStatus] = useState<IIntegrationWebhookStatus>({
    singlewireWebhookUpdateRequired: false,
    everbridgeWebhookUpdateRequired: false,
  });

  useEffect(() => {
    dispatch(getPublicApiApprovalStatus());
  }, [dispatch]);

  useEffect(() => {
    const fetchWebhookStatus = async () => {
      try {
        const emailId = decodeURIComponent(getCookie("email"));
        const response = await api.get(
          `${getIntegrationWebhookStatusUrl}email=${emailId}`,
        );
        setWebhookStatus(response.data);
      } catch {
        // Status check is non-blocking for the integrations page.
      }
    };

    fetchWebhookStatus();
  }, []);

  const navigate = useNavigate();

  return (
    <div className="Configure_container">
      <div className="Configure_content">
        <div className="Configure_header">
          <h1>Integrate 3rd Party Services</h1>
          <OverlayTrigger
            placement="left" 
            overlay={<Tooltip id="integration-help-tooltip">View User Guide</Tooltip>}
          >
            <div 
              className="help-icon-wrapper"
              onClick={() => navigate(routes.userGuide)}
            >
              <HelpIcon />
            </div>
          </OverlayTrigger>
        </div>
        <div className="Configure_body">
          <Row>
            <Col sm={5}>
              <div className="Configure_item">
                <div className="Configure_logo">
                  <img src={THIRD_PARTY_INTEGRATION_LINKS.DAS}></img>
                </div>
                <div className="Configure_details">
                  <h3>Digital Alert Systems</h3>
                  <p>
                    Integrate your D.A.S. system and automatically push your
                    alerts to the FlashCast system and screens.
                  </p>
                  <Button>
                    {" "}
                    <Link to="/integrations/das">Configure</Link>
                  </Button>
                </div>
              </div>
            </Col>
            <Col sm={6}>
              <div className="Configure_item">
                <div className="Configure_logo">
                  <img src={THIRD_PARTY_INTEGRATION_LINKS.INFORMACAST}></img>
                </div>
                <div className="Configure_details">
                  <div className="Configure_details-header">
                    <h3>Singlewire Software</h3>
                    {webhookStatus.singlewireWebhookUpdateRequired && (
                      <span className="integration-action-badge">Action Required</span>
                    )}
                  </div>
                  <p>
                    Integrate your Singlewire Software system and automatically
                    push your alerts to the FlashCast system and screens.
                  </p>
                  <Button>
                    {" "}
                    <Link to="/integrations/singlewire">Configure</Link>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm={5}>
              <div className="Configure_item">
                <div className="Configure_logo">
                  <img src={THIRD_PARTY_INTEGRATION_LINKS.EVERBRIDGE}></img>
                </div>
                <div className="Configure_details">
                  <div className="Configure_details-header">
                    <h3>Everbridge Suite</h3>
                    {webhookStatus.everbridgeWebhookUpdateRequired && (
                      <span className="integration-action-badge">Action Required</span>
                    )}
                  </div>
                  <p>
                    Integrate your Everbridge Suite and automatically push your
                    alerts to the FlashCast system and screens.
                  </p>
                  <Button>
                    {" "}
                    <Link to="/integrations/everbridge">Configure</Link>
                  </Button>
                </div>
              </div>
            </Col>
            <Col sm={6}>
              <div className="Configure_item">
                <div className="Configure_logo">
                  <img src={THIRD_PARTY_INTEGRATION_LINKS.ALERTUS}></img>
                </div>
                <div className="Configure_details">
                  <h3>AlertUs System</h3>
                  <p>
                    Integrate your AlertUs System and automatically push your
                    alerts to the FlashCast system and screens.
                  </p>
                  <Button>
                    {" "}
                    <Link 
                      to="/integrations/alertus"
                    >
                      Configure
                    </Link>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col sm={5}>
              <div className="Configure_item">
                <div className="Configure_logo">
                  <img src={THIRD_PARTY_INTEGRATION_LINKS.CAP} alt="CAP / Rave Alert" />
                </div>
                <div className="Configure_details">
                  <h3>CAP / Rave Alert</h3>
                  <p>
                    Receive Common Alerting Protocol (CAP) XML from producers
                    like Rave Alert and automatically push alerts to the
                    FlashCast system and screens.
                  </p>
                  <Button>
                    {" "}
                    <Link to={routes.capConfigure}>Configure</Link>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="Configure_content">
        <div className="Configure_header">
          <h1>Integrate FlashCast</h1>
        </div>
        <div className="Configure_body">
          <Row>
            <Col sm={5}>
              <div
                className={`Configure_item ${
                  !isSuperAdminApproved ? "disabled" : ""
                }`}
                title={
                  !isSuperAdminApproved
                    ? "Contact super admin for Flashcast Public API Access"
                    : ""
                }
              >
                <div className="Configure_logo">
                  <img src={THIRD_PARTY_INTEGRATION_LINKS.MIMO} alt="FlashCast Public API" />
                </div>
                <div className="Configure_details">
                  <h3>Flashcast Public API</h3>
                  <p>
                    Integrate with your system and automatically push your
                    alerts to the system and screens.
                  </p>
                  <Button disabled={!isSuperAdminApproved}>
                    <Link
                      to="/integrations/flashcast"
                      onClick={(e) => {
                        if (!isSuperAdminApproved) e.preventDefault();
                      }}
                    >
                      Configure
                    </Link>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Integration;
